import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import type { SetRangeFormFields } from './types';
import {
  computeLowerTick,
  computeMaxPercent,
  computeMaxPriceInEth,
  computeMinPercent,
  computeMinPriceInEth,
  computeTick,
  computeUpperTick,
  getMaxPercentages,
  getMinPercentages,
  useComputeAmounts,
} from './utils';
import { Address, MAX_TICK, MIN_TICK, feeTierToTickSpacing } from 'nftx.js';
import { useNetwork } from '@nftx/react';
import useDebounceCallback from '@/presentation/hooks/useDebounceCallback';
import useCollection from '@/application/read/collections/useCollection';
import { isAddressToken0 } from '@/domain/ticks/helpers';
import SetRangeForm from './SetRangeForm';

type Form = UseFormReturn<SetRangeFormFields>;

export interface SetRangeFormProps {
  poolAddress: Address | undefined;
  liquidity: bigint;
  tokenAddress: Address;
  collectionAddress: Address;
  symbol: string;
  feeTier: string;
  apy?: bigint;
  poolType: 'pair' | 'eth' | 'nft';
  vaultId?: string;
  amountMax?: bigint;
  ethMax?: bigint;
  currentTick?: string;
  icon?: string;
  register: Form['register'];
  control: Form['control'];
  errors: Form['formState']['errors'];
  setValue: Form['setValue'];
  isNewVault: boolean;
  isNewPool: boolean;
  using: 'vtoken' | 'nft';
  canEditRange?: boolean;
  setDisabled: (v: boolean) => void;
}

export const SetRangeFormContainer = (props: SetRangeFormProps) => {
  const {
    collectionAddress,
    tokenAddress,
    feeTier,
    currentTick = '',
    control,
    setValue,
    isNewVault,
    isNewPool,
    poolType,
    canEditRange = true,
    setDisabled,
  } = props;
  const network = useNetwork();
  // type of pool
  const isEthOnly = poolType === 'eth';
  const isNftOnly = poolType === 'nft';
  // details around calculating pricing
  const tickSpacing = feeTierToTickSpacing(Number(feeTier));
  const isVToken0 = isAddressToken0(tokenAddress, network);

  // Used for placeholder
  const { data: meta } = useCollection(collectionAddress);

  // watch for when the actual form tick values are updated - source of truth
  const {
    tickLower = MIN_TICK,
    tickUpper = MAX_TICK,
    price = '0',
    fullRange = false,
    minPriceInEth = '',
    maxPriceInEth = '',
    tick = currentTick,
    tokenAmount,
    ethAmount,
    liquidityAmount = '0',
  } = useWatch({
    control,
  });

  const minPercent = useMemo(() => {
    if (!minPriceInEth) {
      return getMinPercentages(poolType)[0];
    }
    return computeMinPercent({ tick, tickLower });
  }, [minPriceInEth, poolType, tick, tickLower]);

  const maxPercent = useMemo(() => {
    if (!maxPriceInEth) {
      return getMaxPercentages(poolType).pop();
    }
    return computeMaxPercent({ tick, tickUpper, isVToken0 });
  }, [isVToken0, maxPriceInEth, poolType, tick, tickUpper]);

  const [amountController, setAmountController] = useState<
    'ethAmount' | 'tokenAmount'
  >('tokenAmount');

  const computeAmounts = useComputeAmounts({
    amountController,
    ethAmount,
    feeTier,
    liquidityAmount,
    network,
    poolType,
    tokenAddress,
    tokenAmount,
  });

  const onPriceChange = useDebounceCallback(
    async (price: string) => {
      if (
        !canEditRange ||
        !price ||
        !Number(price) ||
        Number.isNaN(Number(price))
      ) {
        setDisabled(false);
        return;
      }

      const tick = computeTick({ price, tickSpacing, isVToken0 });

      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('tick', tick);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    500,
    [tickSpacing, computeAmounts, setValue, fullRange]
  );
  const onMinPriceChange = useDebounceCallback(
    async (minPriceInEth: string) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const tickLower = computeLowerTick({
        minPriceInEth,
        tick,
        isNftOnly,
        tickSpacing,
        fullRange,
        network,
        isVToken0,
      });

      // The min price entered by the user may not be exactly on a tick, so we need to recalculate the min price again for an accurate amount
      minPriceInEth = computeMinPriceInEth({ tickLower, isVToken0 });

      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        minPriceInEth,
        maxPriceInEth,
        price,
        fullRange,
      });

      setValue('minPriceInEth', minPriceInEth);
      setValue('tickLower', tickLower);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    500,
    [
      computeAmounts,
      isNftOnly,
      maxPriceInEth,
      price,
      setValue,
      tick,
      tickSpacing,
      fullRange,
      fullRange,
    ]
  );
  const onMaxPriceChange = useDebounceCallback(
    async (maxPriceInEth: string) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const tickUpper = computeUpperTick({
        maxPriceInEth,
        tick,
        isEthOnly,
        tickSpacing,
        fullRange,
        network,
        isVToken0,
      });
      // The max price entered by the user may not be exactly on a tick, so we need to recalculate the max price again for an accurate amount
      maxPriceInEth = computeMaxPriceInEth({ tickUpper, isVToken0 });
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('maxPriceInEth', maxPriceInEth);
      setValue('tickUpper', tickUpper);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    500,
    [
      computeAmounts,
      isEthOnly,
      minPriceInEth,
      price,
      setValue,
      tick,
      tickSpacing,
      fullRange,
    ]
  );
  const onTickUpperChange = useDebounceCallback(
    async (tickUpper: number) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const maxPriceInEth = computeMaxPriceInEth({ tickUpper, isVToken0 });
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('maxPriceInEth', maxPriceInEth);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [computeAmounts, minPriceInEth, price, setValue, tick, fullRange]
  );
  const onTickLowerChange = useDebounceCallback(
    async (tickLower: number) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const minPriceInEth = computeMinPriceInEth({ tickLower, isVToken0 });
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('minPriceInEth', minPriceInEth);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [computeAmounts, maxPriceInEth, price, setValue, tick, fullRange]
  );
  const onMinPercentChange = useDebounceCallback(
    async (minPercent: number) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const tickLower = computeLowerTick({
        minPercent,
        tick,
        isNftOnly,
        tickSpacing,
        fullRange,
        network,
        isVToken0,
      });
      const minPriceInEth = computeMinPriceInEth({ tickLower, isVToken0 });
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('tickLower', tickLower);
      setValue('minPriceInEth', minPriceInEth);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [
      computeAmounts,
      isNftOnly,
      maxPriceInEth,
      price,
      setValue,
      tick,
      tickSpacing,
      fullRange,
    ]
  );
  const onMaxPercentChange = useDebounceCallback(
    async (maxPercent: number) => {
      if (!canEditRange) {
        setDisabled(false);
        return;
      }
      const tickUpper = computeUpperTick({
        maxPercent,
        tick,
        isEthOnly,
        tickSpacing,
        fullRange,
        network,
        isVToken0,
      });
      const maxPriceInEth = computeMaxPriceInEth({ tickUpper, isVToken0 });
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('tickUpper', tickUpper);
      setValue('maxPriceInEth', maxPriceInEth);
      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [
      computeAmounts,
      isEthOnly,
      minPriceInEth,
      price,
      setValue,
      tick,
      tickSpacing,
      fullRange,
    ]
  );
  const onEthAmountChange = useDebounceCallback(
    async (ethAmount: string) => {
      const { tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        ethAmount,
        fullRange,
      });

      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [computeAmounts, maxPriceInEth, minPriceInEth, price, setValue, fullRange]
  );
  const onTokenAmountchange = useDebounceCallback(
    async (tokenAmount: string) => {
      const { ethAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        tokenAmount,
        fullRange,
      });

      setValue('ethAmount', ethAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [computeAmounts, maxPriceInEth, minPriceInEth, price, setValue, fullRange]
  );
  const onFullRangeChange = useDebounceCallback(
    async (fullRange: boolean) => {
      const { ethAmount, tokenAmount, liquidityAmount } = await computeAmounts({
        maxPriceInEth,
        minPriceInEth,
        price,
        fullRange,
      });

      setValue('ethAmount', ethAmount);
      setValue('tokenAmount', tokenAmount);
      setValue('liquidityAmount', liquidityAmount);
      setDisabled(false);
    },
    250,
    [price, maxPriceInEth, minPriceInEth]
  );

  // If the TWAP price changes, we need to recalculate everything
  const priceUseEffectRunRef = useRef(false);
  useEffect(() => {
    if (price && !isNewPool && !isNewVault && priceUseEffectRunRef.current) {
      setDisabled(true);
      onPriceChange(price);
    } else if (!priceUseEffectRunRef.current) {
      priceUseEffectRunRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);

  useEffect(() => {
    (async () => {
      setDisabled(true);
      const data = {
        tick,
        minPriceInEth,
        tickLower,
        ethAmount,
        tokenAmount,
        tickUpper,
        maxPriceInEth,
        liquidityAmount,
      };

      if (canEditRange) {
        if (price && Number(price)) {
          if (currentTick) {
            data.tick = currentTick;
          } else {
            data.tick = computeTick({ price, tickSpacing, isVToken0 });
          }
        }
        if (price && (!minPriceInEth || !Number(minPriceInEth))) {
          data.tickLower = computeLowerTick({
            minPercent: getMinPercentages(poolType)[0],
            tick: data.tick,
            isNftOnly,
            tickSpacing,
            fullRange,
            network,
            isVToken0,
          });
          data.minPriceInEth = computeMinPriceInEth({
            tickLower: data.tickLower,
            isVToken0,
          });
        }
        if (price && (!maxPriceInEth || !Number(maxPriceInEth))) {
          data.tickUpper = computeUpperTick({
            maxPercent: getMaxPercentages(poolType).pop(),
            tick: data.tick,
            isEthOnly,
            tickSpacing,
            fullRange,
            network,
            isVToken0,
          });
          data.maxPriceInEth = computeMaxPriceInEth({
            tickUpper: data.tickUpper,
            isVToken0,
          });
        }
      }
      if (
        (!ethAmount || !Number(ethAmount)) &&
        tokenAmount &&
        Number(tokenAmount) &&
        poolType !== 'nft'
      ) {
        const { ethAmount, liquidityAmount } = await computeAmounts({
          maxPriceInEth: data.maxPriceInEth,
          minPriceInEth: data.minPriceInEth,
          price,
          tokenAmount,
          fullRange,
        });
        data.ethAmount = ethAmount;
        data.liquidityAmount = liquidityAmount;
      } else if (
        (!tokenAmount || !Number(tokenAmount)) &&
        ethAmount &&
        Number(ethAmount) &&
        poolType !== 'eth'
      ) {
        const { tokenAmount, liquidityAmount } = await computeAmounts({
          maxPriceInEth: data.maxPriceInEth,
          minPriceInEth: data.minPriceInEth,
          price,
          ethAmount,
          fullRange,
        });

        data.tokenAmount = tokenAmount;
        data.liquidityAmount = liquidityAmount;
      }

      Object.entries(data).forEach(([key, value]) => {
        setValue(key as any, value);
      });
      setDisabled(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);

  return (
    <SetRangeForm
      {...props}
      onEthAmountChange={onEthAmountChange}
      onTokenAmountchange={onTokenAmountchange}
      setAmountController={setAmountController}
      onMaxPriceChange={onMaxPriceChange}
      onMinPercentChange={onMinPercentChange}
      onMaxPercentChange={onMaxPercentChange}
      onFullRangeChange={onFullRangeChange}
      onPriceChange={onPriceChange}
      onTickLowerChange={onTickLowerChange}
      onTickUpperChange={onTickUpperChange}
      onMinPriceChange={onMinPriceChange}
      collection={meta}
      isVToken0={isVToken0}
    />
  );
};

export default SetRangeFormContainer;
